import { RouterProvider } from 'react-router-dom';
import { Providers } from './providers';
import { router } from './router';
import { BiometricGate } from './BiometricGate';
import { useAppStatusStore } from '@shared/stores/appStatusStore';
import { ErrorState } from '@shared/ui/feedback/ErrorState';

/**
 * Root component. Mounts <Providers>, then <RouterProvider>. Non-React
 * navigation (axios interceptor logout, deep-link handler) goes through
 * the singleton in `shared/lib/navigation.ts`, which currently falls back
 * to `window.location.replace` — Phase B can rewire to `useNavigate` via
 * a route-element binding for smoother UX.
 */
export function App(): JSX.Element {
  const appNotFound = useAppStatusStore((s) => s.notFound);

  // Boot decided the configured app/tenant does NOT exist (resolve-domain 404).
  // Hard-block with a full-screen message instead of rendering the empty/broken
  // shell. Transient failures never set this flag, so an outage won't land here.
  if (appNotFound) {
    return (
      <Providers>
        <div className="flex min-h-screen flex-col items-center justify-center bg-paper">
          <ErrorState
            title="App not found"
            description="The configured app doesn't exist. Check your configuration."
          />
        </div>
      </Providers>
    );
  }

  return (
    <Providers>
      <BiometricGate>
        <RouterProvider router={router} />
      </BiometricGate>
    </Providers>
  );
}
